import axios from "axios";

const DEFAULT_PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 200;
const SIGNED_URL_BATCH_LIMIT = 50;
const DEFAULT_BATCH_CONCURRENCY = 3;

export const listDesignerWorkspaceContents = async ({ path = "", pageSize = DEFAULT_PAGE_SIZE, cursor } = {}) => {
	const normalizedPageSize = Math.min(Math.max(Number(pageSize) || DEFAULT_PAGE_SIZE, 1), MAX_PAGE_SIZE);
	const params = {
		path,
		pageSize: normalizedPageSize,
		_t: Date.now(),
	};

	if (cursor) {
		params.cursor = cursor;
	}

	const { data } = await axios.get("/api/designer_workspace/list_contents", { params });

	const rawPagination = data?.pagination || {};
	const normalizedNextCursor =
		rawPagination.nextCursor ??
		rawPagination.next_cursor ??
		rawPagination.nextPageCursor ??
		rawPagination.next_page_cursor ??
		rawPagination.nextToken ??
		rawPagination.next_token ??
		null;
	const rawHasNextPage =
		rawPagination.hasNextPage ??
		rawPagination.has_next_page ??
		rawPagination.hasMore ??
		rawPagination.has_more;

	return {
		...data,
		pagination: {
			pageSize: rawPagination.pageSize ?? rawPagination.page_size ?? normalizedPageSize,
			nextCursor: normalizedNextCursor,
			hasNextPage: rawHasNextPage === undefined ? Boolean(normalizedNextCursor) : Boolean(rawHasNextPage),
		},
	};
};

export const searchDesignerWorkspaceContents = async ({ path = "", search } = {}) => {
	const { data } = await axios.get("/api/designer_workspace/search_contents", {
		params: {
			path,
			search,
			_t: Date.now(),
		},
	});
	return data;
};

export const getDesignerWorkspaceSignedUrl = async ({ fileName, download = false }) => {
	const { data } = await axios.get("/api/designer_workspace/signed_url", {
		params: {
			fileName,
			download: String(Boolean(download)),
		},
	});
	return data?.url || null;
};

const chunkArray = (items, chunkSize) => {
	const chunks = [];
	for (let i = 0; i < items.length; i += chunkSize) {
		chunks.push(items.slice(i, i + chunkSize));
	}
	return chunks;
};

export const getDesignerWorkspaceSignedUrlsBatch = async ({
	fileNames = [],
	download = false,
	concurrency = DEFAULT_BATCH_CONCURRENCY,
} = {}) => {
	const uniqueFileNames = [...new Set((fileNames || []).filter(Boolean))];
	if (uniqueFileNames.length === 0) return new Map();

	const chunks = chunkArray(uniqueFileNames, SIGNED_URL_BATCH_LIMIT);
	const resultMap = new Map();
	let chunkIndex = 0;
	const workerCount = Math.max(1, Math.min(concurrency, chunks.length));

	const worker = async () => {
		while (chunkIndex < chunks.length) {
			const currentIndex = chunkIndex++;
			const chunk = chunks[currentIndex];
			const { data } = await axios.post("/api/designer_workspace/signed_urls_batch", {
				fileNames: chunk,
				download: Boolean(download),
			});
			(data?.urls || []).forEach((item) => {
				if (item?.fileName && item?.url) {
					resultMap.set(item.fileName, item.url);
				}
			});
		}
	};

	await Promise.all(Array.from({ length: workerCount }, () => worker()));
	return resultMap;
};

export { DEFAULT_BATCH_CONCURRENCY, DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE, SIGNED_URL_BATCH_LIMIT };
